import { ProjectDesignerRoleTags, roleTagTranslationKeys } from '@nohara-dxdev/mt-common';

/**
 * Mapping from human-readable names (Japanese and English) to ProjectDesignerRoleTags enum values
 * Japanese names are from backend/modules/app/config/default-app-roles.ts
 */
const roleNameToTagMapping: Record<string, ProjectDesignerRoleTags> = {
  // Japanese names (from default-app-roles.ts)
  意匠設計責任者: ProjectDesignerRoleTags.LeadArchitectDesigner,
  構造設計責任者: ProjectDesignerRoleTags.LeadStructureDesigner,
  電気設備設計責任者: ProjectDesignerRoleTags.LeadElectricalDesigner,
  機械設備設計責任者: ProjectDesignerRoleTags.LeadMachineryFacilityDesigner,
  意匠設計者: ProjectDesignerRoleTags.ArchitectDesigner,
  構造設計者: ProjectDesignerRoleTags.StructureDesigner,
  電気設備設計者: ProjectDesignerRoleTags.ElectricalFacilityDesigner,
  機械設備設計者: ProjectDesignerRoleTags.MachineryFacilityDesigner,

  // English names (human-readable)
  'Lead Architectural Designer': ProjectDesignerRoleTags.LeadArchitectDesigner,
  'Lead Architect Designer': ProjectDesignerRoleTags.LeadArchitectDesigner,
  'Lead Structural Designer': ProjectDesignerRoleTags.LeadStructureDesigner,
  'Lead Structure Designer': ProjectDesignerRoleTags.LeadStructureDesigner,
  'Lead Electrical Designer': ProjectDesignerRoleTags.LeadElectricalDesigner,
  'Lead Mechanical Designer': ProjectDesignerRoleTags.LeadMachineryFacilityDesigner,
  'Lead Machinery Facility Designer': ProjectDesignerRoleTags.LeadMachineryFacilityDesigner,
  'Architectural Designer': ProjectDesignerRoleTags.ArchitectDesigner,
  'Architect Designer': ProjectDesignerRoleTags.ArchitectDesigner,
  'Structural Designer': ProjectDesignerRoleTags.StructureDesigner,
  'Structure Designer': ProjectDesignerRoleTags.StructureDesigner,
  'Electrical Designer': ProjectDesignerRoleTags.ElectricalFacilityDesigner,
  'Electrical Facility Designer': ProjectDesignerRoleTags.ElectricalFacilityDesigner,
  'Mechanical Designer': ProjectDesignerRoleTags.MachineryFacilityDesigner,
  'Machinery Facility Designer': ProjectDesignerRoleTags.MachineryFacilityDesigner,

  // Case-insensitive variations (lowercase)
  'lead architectural designer': ProjectDesignerRoleTags.LeadArchitectDesigner,
  'lead architect designer': ProjectDesignerRoleTags.LeadArchitectDesigner,
  'lead structural designer': ProjectDesignerRoleTags.LeadStructureDesigner,
  'lead structure designer': ProjectDesignerRoleTags.LeadStructureDesigner,
  'lead electrical designer': ProjectDesignerRoleTags.LeadElectricalDesigner,
  'lead mechanical designer': ProjectDesignerRoleTags.LeadMachineryFacilityDesigner,
  'lead machinery facility designer': ProjectDesignerRoleTags.LeadMachineryFacilityDesigner,
  'architectural designer': ProjectDesignerRoleTags.ArchitectDesigner,
  'architect designer': ProjectDesignerRoleTags.ArchitectDesigner,
  'structural designer': ProjectDesignerRoleTags.StructureDesigner,
  'structure designer': ProjectDesignerRoleTags.StructureDesigner,
  'electrical designer': ProjectDesignerRoleTags.ElectricalFacilityDesigner,
  'electrical facility designer': ProjectDesignerRoleTags.ElectricalFacilityDesigner,
  'mechanical designer': ProjectDesignerRoleTags.MachineryFacilityDesigner,
  'machinery facility designer': ProjectDesignerRoleTags.MachineryFacilityDesigner,

  // Also support the enum values directly (for backward compatibility)
  lead_architect_designer: ProjectDesignerRoleTags.LeadArchitectDesigner,
  lead_structure_designer: ProjectDesignerRoleTags.LeadStructureDesigner,
  lead_electrical_designer: ProjectDesignerRoleTags.LeadElectricalDesigner,
  lead_machinery_facility_designer: ProjectDesignerRoleTags.LeadMachineryFacilityDesigner,
  architect_designer: ProjectDesignerRoleTags.ArchitectDesigner,
  structure_designer: ProjectDesignerRoleTags.StructureDesigner,
  electrical_facility_designer: ProjectDesignerRoleTags.ElectricalFacilityDesigner,
  machinery_facility_designer: ProjectDesignerRoleTags.MachineryFacilityDesigner,
};

/**
 * Map a CSV role name (Japanese or English) to ProjectDesignerRoleTags enum value
 * Supports:
 * - Japanese names from default-app-roles.ts
 * - English human-readable names
 * - Enum values directly
 * - Case-insensitive matching
 */
export const mapRoleNameToTag = (roleName: string): ProjectDesignerRoleTags | null => {
  const trimmedName = roleName.trim();

  // Try exact match first (case-sensitive, for Japanese and exact English)
  if (trimmedName in roleNameToTagMapping) {
    return roleNameToTagMapping[trimmedName];
  }

  // Try case-insensitive match (for English variations)
  const lowerCaseName = trimmedName.toLowerCase();
  if (lowerCaseName in roleNameToTagMapping) {
    return roleNameToTagMapping[lowerCaseName];
  }

  // Check if it's already a valid enum value
  if (isValidRoleTag(trimmedName)) {
    return trimmedName;
  }

  return null;
};

// Get translation key for a role tag
export const getRoleTagTranslationKey = (tag: string): string => {
  return tag in roleTagTranslationKeys
    ? roleTagTranslationKeys[tag as ProjectDesignerRoleTags]
    : tag;
};

// Get all valid role tags as options for CSV validation
export const getValidRoleTagOptions = (): {
  tag: ProjectDesignerRoleTags;
  translationKey: string;
}[] => {
  return Object.entries(ProjectDesignerRoleTags).map(([, tag]) => ({
    tag,
    translationKey: getRoleTagTranslationKey(tag),
  }));
};

// Validate if a tag is valid
export const isValidRoleTag = (tag: string): tag is ProjectDesignerRoleTags => {
  return Object.values(ProjectDesignerRoleTags).includes(tag as ProjectDesignerRoleTags);
};

/**
 * Get all supported role name variations for display/documentation
 */
export const getSupportedRoleNames = (): Record<ProjectDesignerRoleTags, string[]> => {
  const result: Record<string, string[]> = {};

  // Group all names by their target tag
  for (const [name, tag] of Object.entries(roleNameToTagMapping)) {
    if (!(tag in result)) {
      result[tag] = [];
    }
    result[tag].push(name);
  }

  return result as Record<ProjectDesignerRoleTags, string[]>;
};
